import { useNavigate } from '@tanstack/react-router';
import { useDebounce } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page, PageProps } from 'react-pdf';
// @ts-expect-error cannot find module
import { PageCallback } from 'react-pdf/dist/cjs/shared/types.js';

import { Button, Heading, Text } from '@/components/core';
import { HiddenIcon } from '@/components/icons';
import { DEFAULT_SELECTED_NODE } from '@/constants';
import {
  OcrItem,
  useAttentionMap,
  useCanvasOverlay,
  useDrawBoxes,
  useFilterOcrItems,
  useGetAgentFromPath,
  useSelectionBox,
} from '@/hooks';
import { useDocumentStore } from '@/store';
import { attentionMapToBlocks, Box, findModelId } from '@/utils';

import { OcrPopover } from './OcrPopover';

export type DocumentPageProps = {
  page: number;
} & PageProps;

const IDocumentPage = ({ page, ...rest }: DocumentPageProps) => {
  const zoom = useDocumentStore((state) => state.zoom);
  const focusedField = useDocumentStore((state) => state.focusedField);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rotation = useDocumentStore((state) => state.rotation);
  const predictedPages = useDocumentStore((state) => state.predictedPages);
  const showPages = useDocumentStore((state) => state.showPages);
  const setShowPages = useDocumentStore((state) => state.setShowPages);

  const [baseRotation, setBaseRotation] = useState(0);
  const [renderSuccess, setRenderSuccess] = useState(false);
  const [canvasVersion, setCanvasVersion] = useState(0);

  const isPageHidden = useMemo(() => {
    if (showPages && showPages.includes(page - 1)) return false;
    return predictedPages ? !predictedPages.includes(page - 1) : false;
  }, [page, predictedPages, showPages]);

  const showPage = useCallback(() => {
    const pages = showPages ?? [];
    pages.push(page - 1);
    setShowPages(Array.from(new Set(pages)));
  }, [page, setShowPages, showPages]);

  useEffect(() => setRenderSuccess(false), [rotation, zoom]);

  const attentionMap = useMemo(
    () => (page - 1 === focusedField?.page ? attentionMapToBlocks(focusedField?.attentionMap) : []),
    [focusedField?.attentionMap, focusedField?.page, page]
  );

  const { overlayRef: attentionMapCanvasRef, layerVersion: attentionMapLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  useAttentionMap(attentionMapCanvasRef, attentionMap, rotation, attentionMapLayerVersion);

  const { overlayRef: ocrLayerCanvasRef, layerVersion: ocrLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [ocrItems, setOcrItems] = useState<OcrItem[]>([]);
  useDrawBoxes(ocrLayerCanvasRef, ocrItems, rotation, ocrLayerVersion);

  const { overlayRef: selectionCanvasRef, layerVersion: selectionLayerVersion } = useCanvasOverlay({
    canvasRef,
    canvasVersion,
  });
  const [selectionBox, setSelectionBox] = useState<Box | undefined>();
  const debouncedSelectionBox = useDebounce(selectionBox, 50);

  const { handleMouseDown, handleMouseMove, handleMouseUp, clear } = useSelectionBox({
    canvasRef: selectionCanvasRef,
    onSelectMove: (box) => setSelectionBox(box),
    onSelectEnd: (box) => setSelectionBox(box),
    onSelectStart: (box) => setSelectionBox(box),
    canvasVersion: selectionLayerVersion,
  });

  const {
    items: filteredOcrItems,
    anchor: ocrAnchor,
    text: ocrText,
  } = useFilterOcrItems({
    selectionBox: debouncedSelectionBox,
    canvasRef,
    rotation,
    page: page - 1,
    canvasVersion: selectionLayerVersion,
    scaleAnchorByDpr: true,
  });

  useEffect(() => setOcrItems(filteredOcrItems), [filteredOcrItems]);
  const dismissOcrPopover = useCallback(() => {
    setSelectionBox(undefined);
    clear();
  }, [clear]);

  const onRenderSuccess = useCallback((page: PageCallback) => {
    setBaseRotation(page.rotate);
    setRenderSuccess(true);
    setCanvasVersion((v) => v + 1);
  }, []);

  const { data: agent } = useGetAgentFromPath();
  const navigate = useNavigate();

  const goToModelSettings = useCallback(async () => {
    if (!agent) return;

    const modelId = findModelId(agent);
    if (modelId) sessionStorage.setItem(DEFAULT_SELECTED_NODE, modelId);

    return navigate({ to: '/agents/$agentId/integration', params: { agentId: agent.agentId } });
  }, [agent, navigate]);

  return (
    <div
      className={!renderSuccess ? 'hidden' : 'animate-fade-in-fast flex w-full min-w-fit justify-center bg-gray-200!'}
    >
      <Page
        key={page}
        canvasRef={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onRenderSuccess={onRenderSuccess}
        pageNumber={page}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        rotate={baseRotation + rotation}
        scale={zoom}
        {...rest}
      >
        {ocrAnchor && ocrText ? <OcrPopover anchor={ocrAnchor} text={ocrText} onDismiss={dismissOcrPopover} /> : null}
        {isPageHidden ? (
          <div className="absolute top-0 left-0 z-20 flex h-full w-full items-center justify-center bg-gray-200/75 backdrop-blur-xs">
            <div className="gap-sm flex w-64 flex-col items-center justify-center rounded-lg bg-black/80 p-5">
              <HiddenIcon className="size-6 text-white" />

              <Heading className="text-gray-25">The AI didn't read this page</Heading>

              <Text className="text-gray-100">
                This agent is set to read up to {predictedPages?.length ?? 0} pages per document. You can change this in{' '}
                <span onClick={goToModelSettings} className="cursor-pointer font-semibold underline">
                  agent settings.
                </span>
              </Text>

              <Button variant="plain" className="mt-3 w-full border border-gray-500 bg-gray-800" onPress={showPage}>
                <Text className="!text-gray-200 group-hover/button:!text-gray-700 group-data-[pressed]/button:!text-gray-700">
                  Show anyway
                </Text>
              </Button>
            </div>
          </div>
        ) : null}
      </Page>
    </div>
  );
};

export const DocumentPage = memo(IDocumentPage) as typeof IDocumentPage;
